import { useMemo } from 'react';

import { SideItemOption, SideItemType } from '@common/enums';
import { ArrowInfo, DrawingShape } from '@common/models';

import SideItemRadioList from '../MultiSideItems/MultiSideItems';

interface ShapeSideItemsProps {
    shape: DrawingShape;
}

const emptyArray = [] satisfies ArrowInfo[];

export interface SideItemSettings {
    arrowInfo: ArrowInfo;
    waterfallHeight: number | undefined;
    backsplashHeight: number | undefined;
    sideOptionType: SideItemOption | undefined;
    selectedSideItemType: SideItemType | undefined;
}

export const ShapeSideItems = ({ shape }: ShapeSideItemsProps) => {
    const sortedSideItems = useMemo(() => {
        if (!shape?.arrowsInfo) {
            return emptyArray;
        }
        return shape.arrowsInfo
            .filter((arrowInfo) => !(arrowInfo.isInterior || arrowInfo.isDiagonalCorner))
            .toSorted((arrowInfoA, arrowInfoB) => {
                const label1 = arrowInfoA.label ?? '';
                const label2 = arrowInfoB.label ?? '';
                // Sort by length and then alphabetically
                return Math.sign(label1.length - label2.length) || label1.localeCompare(label2);
            })
            .map((arrowInfo) => {
                const removedEdge = shape.removedElementsHistory?.edgesAndCorners?.find(
                    (ec) => ec.arrowInfo?.label === arrowInfo.label
                );
                const drawingEdgeSideItem = removedEdge?.drawingEdge.sideItem;
                return {
                    arrowInfo,
                    ...(removedEdge ? { selectedSideItemType: removedEdge.sideItemType } : {}),
                    ...(drawingEdgeSideItem
                        ? {
                              backsplashHeight:
                                  drawingEdgeSideItem.type === SideItemOption.BACKSPLASH
                                      ? drawingEdgeSideItem.depth
                                      : undefined,
                              waterfallHeight:
                                  drawingEdgeSideItem.type === SideItemOption.WATERFALL
                                      ? drawingEdgeSideItem.depth
                                      : undefined,
                              depth: drawingEdgeSideItem.depth,
                              sideOptionType: drawingEdgeSideItem.type,
                          }
                        : {}),
                } as SideItemSettings;
            });
    }, [shape.arrowsInfo, shape.removedElementsHistory?.edgesAndCorners]);

    return (
        <>
            {sortedSideItems?.map((side, index) => (
                <SideItemRadioList
                    key={index}
                    shape={shape}
                    arrowInfo={side.arrowInfo}
                    backsplashHeight={side.backsplashHeight}
                    waterfallHeight={side.waterfallHeight}
                    sideOptionType={side.sideOptionType}
                    selectedSideItemType={side.selectedSideItemType}
                />
            ))}
        </>
    );
};
